// Bill detail page (issue #136).
//
// /bills makes each result clickable to here; the API's `/api/bills/{id}`
// endpoint also returns `movers_linked`/`sponsors_linked` (DisplayName ->
// PHID) so sponsoring members link to their /parliamentarians/:phid pages.

import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, Divider,
  Table, TableBody, TableRow, TableCell, Link, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GavelIcon from '@mui/icons-material/Gavel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getBill } from '../services/api';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';

function statusColor(status) {
  const s = (status || '').toLowerCase();
  if (s.includes('assent')) return 'success';
  if (s.includes('negativ') || s.includes('lapsed') || s.includes('withdraw')) return 'error';
  if (s.includes('before') || s.includes('progress')) return 'info';
  return 'default';
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

// Render a list of {name, phid} as RouterLinks where the PHID resolved,
// plain text otherwise.
function LinkedMembers({ people }) {
  if (!people || people.length === 0) return null;
  return (
    <>
      {people.map((p, i) => (
        <React.Fragment key={`${p.name}-${i}`}>
          {i > 0 && ', '}
          {p.phid ? (
            <Link
              component={RouterLink}
              to={`/parliamentarians/${p.phid}`}
              underline="hover"
            >
              {p.name}
            </Link>
          ) : (
            p.name
          )}
        </React.Fragment>
      ))}
    </>
  );
}

function MetaRow({ label, value }) {
  if (value === null || value === undefined || value === '' ||
      (Array.isArray(value) && value.length === 0)) {
    return null;
  }
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600, width: '30%', verticalAlign: 'top' }}>
        {label}
      </TableCell>
      <TableCell>{Array.isArray(value) ? value.join(', ') : value}</TableCell>
    </TableRow>
  );
}

export default function BillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getBill(id);
        if (active) setBill(data);
      } catch (e) {
        if (active) setError('Bill not found or failed to load.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !bill) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error || 'Bill not found.'}</Alert>
      </Box>
    );
  }

  const movers = bill.movers_linked && bill.movers_linked.length
    ? bill.movers_linked
    : (bill.movers || []).map((n) => ({ name: n, phid: null }));
  const sponsors = bill.sponsors_linked && bill.sponsors_linked.length
    ? bill.sponsors_linked
    : (bill.sponsors || []).map((n) => ({ name: n, phid: null }));

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>

      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
          <GavelIcon sx={{ fontSize: 36, color: 'primary.main', mt: 0.5 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography component="h1" variant="h5">
              {bill.title || 'Untitled Bill'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {bill.status && (
                <Chip label={bill.status} size="small" color={statusColor(bill.status)} />
              )}
              {bill.originating_chamber && (
                <Chip
                  label={bill.originating_chamber}
                  size="small"
                  variant="outlined"
                  color={bill.originating_chamber === 'Senate' ? 'error' : 'primary'}
                />
              )}
              {bill.bill_type && (
                <Chip label={`${bill.bill_type} Bill`} size="small" variant="outlined" />
              )}
            </Box>
          </Box>
          <CopyLinkButton />
          <ShareButton title={bill.title} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Table size="small">
          <TableBody>
            <MetaRow label="Bill ID" value={bill.bill_id} />
            <MetaRow label="Status" value={bill.status} />
            <MetaRow label="Portfolio" value={bill.portfolio} />
            {movers.length > 0 && (
              <TableRow>
                <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                  Movers
                </TableCell>
                <TableCell><LinkedMembers people={movers} /></TableCell>
              </TableRow>
            )}
            {sponsors.length > 0 && (
              <TableRow>
                <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                  Sponsors
                </TableCell>
                <TableCell><LinkedMembers people={sponsors} /></TableCell>
              </TableRow>
            )}
            <MetaRow
              label="Parliament"
              value={bill.parliament_number
                ? <Link component={RouterLink}
                        to={`/parliaments/${bill.parliament_number}`}
                        underline="hover">
                    Parliament {bill.parliament_number}
                  </Link>
                : null}
            />
            <MetaRow label="Last Updated" value={formatDate(bill.last_updated)} />
            <MetaRow label="Amendments" value={bill.amendment_count} />
            <MetaRow label="Related Documents" value={bill.document_count} />
          </TableBody>
        </Table>

        {bill.content && (
          <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'grey.50', maxHeight: 480, overflow: 'auto' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Content
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {bill.content}
            </Typography>
          </Paper>
        )}

        {(bill.parlinfo_url || bill.source_url) && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon />}
              href={bill.parlinfo_url || bill.source_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View on Parliament of Australia
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
